import React, { useState, useEffect } from 'react';
import {
  FileText,
  Plus,
  Eye,
  Edit,
  Trash2,
  Search,
  Filter,
  Download,
  Users,
  GraduationCap,
  BookOpen,
  Calendar,
  X,
  Clock,
  User,
  CheckCircle,
  AlertCircle,
  PlayCircle
} from 'lucide-react';

export default function UnitManagement() {
  const [units, setUnits] = useState([]);
  const [courses, setCourses] = useState([]);
  const [lecturers, setLecturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [unitStats, setUnitStats] = useState({});

  // Create form state
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    courseId: '',
    lecturerId: '',
    status: 'upcoming'
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';

      // Fetch all data in parallel
      const [unitsResponse, coursesResponse, lecturersResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/units`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }),
        fetch(`${API_BASE_URL}/courses`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }),
        fetch(`${API_BASE_URL}/lecturers`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      ]);

      if (!unitsResponse.ok) throw new Error('Failed to fetch units');

      const unitsData = await unitsResponse.json();
      const coursesData = coursesResponse.ok ? await coursesResponse.json() : [];
      const lecturersData = lecturersResponse.ok ? await lecturersResponse.json() : [];

      setUnits(unitsData);
      setCourses(coursesData);
      setLecturers(lecturersData);

      // Fetch stats for each unit (enrolled students)
      const statsPromises = unitsData.map(async (unit) => {
        try {
          const studentsResponse = await fetch(`${API_BASE_URL}/students?unitId=${unit.id}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          const students = studentsResponse.ok ? await studentsResponse.json() : [];

          return {
            unitId: unit.id,
            students: students.length || 0
          };
        } catch (err) {
          return {
            unitId: unit.id,
            students: 0
          };
        }
      });

      const stats = await Promise.all(statsPromises);
      const statsMap = {};
      stats.forEach(stat => {
        statsMap[stat.unitId] = {
          students: stat.students
        };
      });
      setUnitStats(statsMap);

    } catch (error) {
      setError(error.message);
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateUnit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
      const response = await fetch(`${API_BASE_URL}/units`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...createForm,
          courseId: parseInt(createForm.courseId),
          lecturerId: createForm.lecturerId ? parseInt(createForm.lecturerId) : null
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create unit');
      }

      const newUnit = await response.json();
      setUnits(prev => [...prev, newUnit]);
      setCreateForm({ name: '', description: '', courseId: '', lecturerId: '', status: 'upcoming' });
      setShowCreateForm(false);

    } catch (error) {
      setError('Failed to create unit: ' + error.message);
      console.error('Error creating unit:', error);
    }
  };

  const handleViewDetails = async (unit) => {
    try {
      // Fetch detailed unit data
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
      const response = await fetch(`${API_BASE_URL}/units/${unit.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch unit details');
      }

      const detailedUnit = await response.json();

      // Find course and lecturer data
      const course = courses.find(c => c.id === unit.courseId);
      const lecturer = lecturers.find(l => l.id === unit.lecturerId);

      setSelectedUnit({
        ...detailedUnit,
        course: course,
        lecturer: lecturer,
        stats: unitStats[unit.id] || { students: 0 }
      });
      setShowDetailsModal(true);

    } catch (error) {
      setError('Failed to fetch unit details: ' + error.message);
      console.error('Error fetching unit details:', error);
    }
  };

  const handleDeleteUnit = async (unitId) => {
    if (!window.confirm('Are you sure you want to delete this unit? This will affect enrolled students.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';
      const response = await fetch(`${API_BASE_URL}/units/${unitId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete unit');
      }

      setUnits(prev => prev.filter(unit => unit.id !== unitId));

    } catch (error) {
      setError('Failed to delete unit: ' + error.message);
      console.error('Error deleting unit:', error);
    }
  };

  const filteredUnits = units.filter(unit => {
    const matchesSearch = unit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      unit.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === '' || unit.status === statusFilter;
    const matchesCourse = courseFilter === '' || unit.courseId === parseInt(courseFilter);

    return matchesSearch && matchesStatus && matchesCourse;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <PlayCircle className="h-4 w-4" />;
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-yellow-100 text-yellow-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-green-600 to-blue-700 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center">
              <FileText className="mr-3 h-8 w-8" />
              Unit Management
            </h1>
            <p className="mt-2 text-green-100">
              Manage course units and track progress
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">{units.length}</div>
            <div className="text-green-100">Total Units</div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <div className="text-red-600 text-sm">{error}</div>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Controls Section */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search units..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="upcoming">Upcoming</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
            </select>

            <select
              value={courseFilter}
              onChange={(e) => setCourseFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="">All Courses</option>
              {courses.map((course) => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            <button className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              <Download className="h-4 w-4 mr-2" />
              Export
            </button>
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Unit
            </button>
          </div>
        </div>
      </div>

      {/* Units Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredUnits.map((unit) => {
          const stats = unitStats[unit.id] || { students: 0 };
          const course = courses.find(c => c.id === unit.courseId);
          const lecturer = lecturers.find(l => l.id === unit.lecturerId);

          return (
            <div key={unit.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-600 rounded-lg flex items-center justify-center text-white">
                    <FileText className="h-6 w-6" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-semibold text-gray-900">{unit.name}</h3>
                    <p className="text-sm text-gray-500">{course?.name || 'No course assigned'}</p>
                  </div>
                </div>

                <div className="flex gap-1">
                  <button
                    onClick={() => handleViewDetails(unit)}
                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="View Details"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteUnit(unit.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete Unit"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Status and Stats */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(unit.status)}`}>
                    {getStatusIcon(unit.status)}
                    {unit.status.charAt(0).toUpperCase() + unit.status.slice(1)}
                  </span>
                  <div className="text-sm text-gray-500">
                    {stats.students} students enrolled
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <GraduationCap className="h-4 w-4 text-blue-600 mx-auto mb-1" />
                    <div className="text-xl font-bold text-blue-900">{stats.students}</div>
                    <div className="text-xs text-blue-700">Students</div>
                  </div>

                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <User className="h-4 w-4 text-purple-600 mx-auto mb-1" />
                    <div className="text-xl font-bold text-purple-900">
                      {lecturer ? '1' : '0'}
                    </div>
                    <div className="text-xs text-purple-700">Lecturer</div>
                  </div>
                </div>

                {lecturer && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Lecturer:</span> {lecturer.name}
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="text-xs text-gray-500">
                  Created: {new Date(unit.createdAt).toLocaleDateString()}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create Unit Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Create New Unit</h2>
              <button
                onClick={() => setShowCreateForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleCreateUnit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unit Name
                </label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  rows="3"
                  placeholder="Unit description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Course
                </label>
                <select
                  value={createForm.courseId}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, courseId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                >
                  <option value="">Select a course...</option>
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>{course.name}</option>
                  ))}
                </select>
              </div>



              {/* Conditional rendering based on selected course type */}
              {(() => {
                const selectedCourse = courses.find(c => c.id === parseInt(createForm.courseId));
                const isSelfPaced = selectedCourse?.selfPacedEnabled;

                return (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {isSelfPaced ? 'Assign Tutor (Optional)' : 'Assign Lecturer (Optional)'}
                      </label>
                      <select
                        value={createForm.lecturerId}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, lecturerId: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="">{isSelfPaced ? 'Select Tutor' : 'Select Lecturer'}</option>
                        {lecturers.map((lecturer) => (
                          <option key={lecturer.id} value={lecturer.id}>{lecturer.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      {isSelfPaced ? (
                        <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-600 text-sm">
                          <span className="font-medium text-purple-600">Auto-Published</span>
                          <span className="ml-2">- Units in self-paced courses are automatically published.</span>
                        </div>
                      ) : (
                        <select
                          value={createForm.status}
                          onChange={(e) => setCreateForm(prev => ({ ...prev, status: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        >
                          <option value="upcoming">Upcoming</option>
                          <option value="active">Active</option>
                          <option value="completed">Completed</option>
                        </select>
                      )}
                    </div>
                  </>
                );
              })()}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Create Unit
                </button>
              </div>
            </form>
          </div>
        </div >
      )
      }

      {/* Unit Details Modal */}
      {
        showDetailsModal && selectedUnit && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold flex items-center">
                  <FileText className="h-6 w-6 mr-2 text-green-600" />
                  {selectedUnit.name}
                </h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Unit Information */}
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Unit Information</h3>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div>
                        <span className="text-sm font-medium text-gray-600">Description:</span>
                        <p className="text-gray-900 mt-1">{selectedUnit.description || 'No description provided'}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-600">Course:</span>
                        <p className="text-gray-900 mt-1">{selectedUnit.course?.name || 'No course assigned'}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-gray-600">Status:</span>
                        <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedUnit.status)}`}>
                          {selectedUnit.status.charAt(0).toUpperCase() + selectedUnit.status.slice(1)}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                        <span className="text-gray-700">
                          Created: {new Date(selectedUnit.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lecturer and Statistics */}
                <div className="space-y-4">
                  {/* Lecturer Information */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Assigned Lecturer</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      {selectedUnit.lecturer ? (
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-medium">
                            {selectedUnit.lecturer.name.charAt(0)}
                          </div>
                          <div className="ml-3">
                            <p className="font-medium text-gray-900">{selectedUnit.lecturer.name}</p>
                            <p className="text-sm text-gray-500">{selectedUnit.lecturer.email}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <User className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500">No lecturer assigned</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Statistics */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Statistics</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-blue-50 p-4 rounded-lg text-center">
                        <GraduationCap className="h-6 w-6 text-blue-600 mx-auto mb-1" />
                        <div className="text-2xl font-bold text-blue-900">{selectedUnit.stats.students}</div>
                        <div className="text-sm text-blue-700">Enrolled Students</div>
                      </div>

                      <div className="bg-green-50 p-4 rounded-lg text-center">
                        <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-1" />
                        <div className="text-2xl font-bold text-green-900">
                          {selectedUnit.status === 'completed' ? '100%' : selectedUnit.status === 'active' ? '50%' : '0%'}
                        </div>
                        <div className="text-sm text-green-700">Progress</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                  Edit Unit
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Empty State */}
      {
        filteredUnits.length === 0 && !loading && (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No units found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || statusFilter || courseFilter
                ? 'Try adjusting your search and filter criteria.'
                : 'Get started by creating your first unit.'}
            </p>
            {!searchTerm && !statusFilter && !courseFilter && (
              <button
                onClick={() => setShowCreateForm(true)}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Unit
              </button>
            )}
          </div>
        )
      }
    </div >
  );
}
